import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { Case, PatientInsurance, SpecimenAdmin } from "pwapi";

import { useAppDispatch, useAppSelector } from "app/hooks";
import { fetchCase } from "app/reducers/admin/actions/caseActions";
import { deleteInsurance } from "app/reducers/admin/actions/patientActions";
import {
  createSpecimen,
  deleteSpecimen,
  updateSpecimen,
} from "app/reducers/admin/actions/specimensActions";
import { mapAdminCaseToResources } from "app/reducers/admin/selectors/caseDetailSelector";
import AccessionSidebar from "ui/accessioning/accessionSidebar";
import Spinner from "ui/components/Spinner";

import CaseDetailsForm from "./Forms/CaseDetailsForm";
import InsuranceForm from "./Forms/InsuranceForm";
import IntegrationsForm from "./Forms/IntegrationsForm";
import CasePatientForm from "./Forms/PatientForm";
import CasePatientGuarantorForm from "./Forms/PatientGuarantorForm";
import WarningDialog from "./WarningDialog";

const CaseEditor = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { case_id } = useParams();
  const { patient, specimens, documents, ...rest } = useAppSelector(
    mapAdminCaseToResources
  );

  const [insuranceList, setInsuranceList] = useState<PatientInsurance[]>([
    {} as PatientInsurance,
  ]);
  const [warningDialogProps, setWarningDialogProps] = useState<
    | {
        resourceName: string;
        resourceId: number;
        onHandleDelete: Function;
      }
    | undefined
  >();

  const onHandleDeleteSpecimen = (specimen_id: number) => {
    dispatch(deleteSpecimen({ id: specimen_id }));
  };
  const onHandleDeleteInsurance = (insurance_id?: number) => {
    insurance_id
      ? dispatch(deleteInsurance({ id: insurance_id }))
      : setInsuranceList(insuranceList.slice(0, -1));
  };

  useEffect(() => {
    dispatch(fetchCase({ id: case_id ? parseInt(case_id) : 0 })).then(
      (response) => {
        if (
          response?.status === Case.status.FINALIZED ||
          response?.status === Case.status.ARCHIVED ||
          response?.status === Case.status.DELETED ||
          response?.status === Case.status.LIS_PENDING
        ) {
          navigate(`/cases/${response.id}`, { replace: true });
        }
      }
    );
  }, [case_id]);

  useEffect(() => {
    // @ts-ignore
    setInsuranceList(
      patient.insurance && patient.insurance?.length > 0
        ? (patient.insurance as unknown as PatientInsurance[])
        : [{} as PatientInsurance]
    );
  }, [patient]);

  return !rest.isLoading ? (
    <div className="Accession CaseEditor">
      {warningDialogProps && (
        <WarningDialog
          {...warningDialogProps}
          onHandleClose={() => setWarningDialogProps(undefined)}
        />
      )}
      <div className="LISForm">
        <h1 className="LISForm__title">
          <Link
            to="/cases"
            onClick={(e) => {
              e.preventDefault();
              navigate(-1);
            }}
          >
            <figure className="Logo">
              <img src="/logo.svg" alt="PathologyWatch" />
            </figure>
          </Link>
          Edit {rest.accession || "Case"}
        </h1>
        <IntegrationsForm caseDetail={rest} patient={patient} />
        <CaseDetailsForm caseDetail={rest} patient={patient} />
        <CasePatientForm caseDetail={rest} patient={patient} />
        <CasePatientGuarantorForm
          caseDetail={rest}
          patient_id={patient?.id}
          guarantor={patient?.guarantor?.[0]}
        />
        {insuranceList.map((insurance, i: number) => (
          <InsuranceForm
            key={`insurance__${insurance.id || i}`}
            index={i}
            primary={i === 0}
            insurance={insurance}
            patientId={patient?.id}
            lab={rest.accessioning_lab}
            onHandleDeleteInsurance={onHandleDeleteInsurance}
            setWarningDialogProps={setWarningDialogProps}
          />
        ))}

        <div style={{ padding: "1em 0" }}>
          <button
            type="button"
            className="Button Button--purple"
            onClick={() => {
              setInsuranceList([...insuranceList, {} as PatientInsurance]);
            }}
            disabled={
              insuranceList.length >= 5 ||
              !!insuranceList.find((insurance) => !insurance.id)
            }
          >
            Add Additional Insurance
          </button>
        </div>
      </div>
      <AccessionSidebar
        onSave={(specimen: SpecimenAdmin) => {
          dispatch(
            specimen.id && rest.id
              ? updateSpecimen({
                  id: specimen.id,
                  requestBody: { ...specimen, case: rest.id },
                  caseId: rest.id,
                })
              : createSpecimen({
                  requestBody: { ...specimen, case: rest.id || -1 },
                })
          );
        }}
        onDelete={({ specimen }: { specimen: SpecimenAdmin }) => {
          setWarningDialogProps({
            resourceName: `Specimen ${specimen.name}`,
            resourceId: specimen.id || -1,
            onHandleDelete: onHandleDeleteSpecimen,
          });
        }}
        specimens={specimens || []}
        hideSubmitButton
        documents={documents}
        case_id={rest.id}
      />
    </div>
  ) : (
    <Spinner />
  );
};

export default CaseEditor;
